import {
  useContext,
  useRef,
  forwardRef,
  ComponentPropsWithoutRef,
} from "react";
import cx from "classnames";
import { core } from "@servicetitan/hammer-token";

import { DataTrackingId } from "../../types";
import { Icon, IconProps } from "../Icon";
import { motion, useReducedMotion } from "motion/react";
import { useMergeRefs, useTrackingId } from "../../hooks";

import { SegmentedControlContext } from "./internal/SegmentedControlContext";

import styles from "./SegmentedControl.module.scss";

/**
 * State object returned by segment interactions
 */
export type SegmentState = {
  /**
   * Whether the segment is checked
   */
  checked: boolean;
  /**
   * The value of the segment
   */
  value?: SegmentedControlSegmentProps["value"];
};

/**
 * Props for the SegmentedControlSegment component
 * @extends ComponentPropsWithoutRef<"label">
 */
export type SegmentedControlSegmentProps = ComponentPropsWithoutRef<"label"> & {
  /**
   * Optional icon to display in the segment
   */
  icon?: IconProps["svg"];
  /**
   * Value of the segment
   */
  value: string;
  /**
   * The content to display in the segment
   */
  children?: React.ReactNode;
} & DataTrackingId;

/**
 * SegmentedControlSegment component for individual segments within a segmented control.
 *
 * Features:
 * - Individual segment with radio input
 * - Optional icon support
 * - Animated selection indicator
 * - Accessibility support with proper ARIA attributes
 * - Context integration with parent SegmentedControl
 * - Motion animations with reduced motion support
 * - Icon-only mode when no children provided
 * - Data tracking id support
 *
 * @example
 * <SegmentedControl.Segment value="option1" icon={Icon1}>
 *   Option 1
 * </SegmentedControl.Segment>
 *
 * @example
 * <SegmentedControl.Segment value="icon-only" icon={IconOnly} />
 */
export const SegmentedControlSegment = forwardRef<
  HTMLLabelElement,
  SegmentedControlSegmentProps
>((props, ref) => {
  const {
    icon,
    value,
    children,
    className,
    "aria-label": ariaLabel,
    ...rest
  } = props;
  const shouldReduceMotion = useReducedMotion();
  const { current, setCurrent, name } = useContext(SegmentedControlContext);
  const labelRef = useRef<HTMLSpanElement>(null);

  const classNames = cx(styles.segment, className, {
    [styles["icon-only"]]: icon && !children,
  });

  const data = {
    value: props.value,
  };

  const trackingId = useTrackingId({
    name: "SegmentedControlSegment",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  // For icon-only segments (no children), use aria-label if provided, otherwise fall back to value
  const computedAriaLabel = !children ? ariaLabel || value : undefined;

  return (
    <>
      <label
        className={classNames}
        ref={useMergeRefs([labelRef, ref])}
        data-tracking-id={trackingId}
        data-anv="segmented-control-segment"
        aria-label={computedAriaLabel}
        {...rest}
      >
        <input
          type="radio"
          name={name}
          value={value}
          onChange={() => setCurrent?.(value)}
          checked={current === value}
          aria-checked={current === value}
        />
        {icon ? <Icon svg={icon} aria-hidden /> : null}
        {children}
        {current === value && (
          <motion.span
            layoutId={name}
            className={styles["select-indicator"]}
            transition={{
              duration: shouldReduceMotion
                ? 0
                : +core.primitive.Duration.value.replace("ms", "") / 1000,
            }}
          />
        )}
      </label>
    </>
  );
});

SegmentedControlSegment.displayName = "SegmentedControlSegment";
